import React, { useState, useRef, useEffect } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebookF, faInstagram, faYoutube, faTwitter } from '@fortawesome/free-brands-svg-icons';
import { faSearch, faPhone, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import logoImg from '../assets/logo.png';

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const menuRef = useRef(null);
  const hamburgerRef = useRef(null);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const toggleSearch = () => {
    setSearchOpen(!searchOpen);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    alert(`Searching for: ${searchQuery}`);
    setSearchQuery('');
    setSearchOpen(false);
  };

  const isTourActive = location.pathname === '/domestic-tours' || location.pathname === '/international-tours';

  useEffect(() => {
    if (menuOpen) {
      const handleClickOutside = (event) => {
        if (
          menuRef.current &&
          !menuRef.current.contains(event.target) &&
          hamburgerRef.current &&
          !hamburgerRef.current.contains(event.target)
        ) {
          setMenuOpen(false);
        }
      };

      document.addEventListener('click', handleClickOutside);

      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [menuOpen]);

  return (
    <>
      <style>{`
        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }

        /* Top Header Styles */
        .top-header {
          background: linear-gradient(135deg, #023e8a, #00b4d8);
          padding: clamp(8px, 2vw, 12px) clamp(15px, 4vw, 20px);
          display: flex;
          justify-content: space-between;
          align-items: center;
          color: white;
          font-family: 'Poppins', sans-serif;
          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
          border-radius: 0 0 8px 8px;
          flex-wrap: wrap;
        }

        .top-left, .top-right {
          display: flex;
          align-items: center;
          gap: clamp(10px, 2.5vw, 15px);
          flex-wrap: wrap;
        }

        .top-contact {
          display: flex;
          align-items: center;
          gap: clamp(5px, 1vw, 8px);
          font-size: clamp(12px, 1.8vw, 14px);
          font-weight: 600;
          color: rgba(255, 255, 255, 0.9);
          text-decoration: none;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          white-space: nowrap;
        }

        .top-contact:hover {
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
        }

        .top-contact svg {
          font-size: clamp(12px, 1.6vw, 14px);
          flex-shrink: 0;
        }

        .contact-text {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .social-icons {
          display: flex;
          gap: clamp(6px, 1.5vw, 10px);
          flex-wrap: wrap;
        }

        .social-icon {
          color: rgba(255, 255, 255, 0.8);
          font-size: clamp(12px, 1.5vw, 14px);
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          width: clamp(24px, 2.8vw, 28px);
          height: clamp(24px, 2.8vw, 28px);
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.15);
          border: 1px solid rgba(255, 255, 255, 0.3);
          flex-shrink: 0;
        }

        .social-icon:hover {
          color: #31378f;
          transform: scale(1.15);
          background: rgba(255, 255, 255, 0.3);
          border-color: #31378f;
        }

        /* Main Navbar Styles */
        .navbar {
          background: white;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
          padding: 0 5%;
          position: sticky;
          top: 0;
          z-index: 1000;
          transition: all 0.3s ease;
        }

        .navbar-container {
          display: flex;
          align-items: center;
          justify-content: space-between;
          height: 80px;
          max-width: 1400px;
          margin: 0 auto;
        }

        .logo-img {
          height: 70px;
          background: none;
          transition: all 0.3s ease;
        }

        .nav-main {
          display: flex;
          align-items: center;
          gap: 30px;
        }

        .nav-links {
          display: flex;
          align-items: center;
          gap: 25px;
        }

        .nav-links a, .nav-link {
          font-size: 20px;
          font-weight: 700;
          text-decoration: none;
          color: #080808ff;
          position: relative;
          padding: 5px 0;
          transition: all 0.3s ease;
        }

        .nav-links a:after, .nav-link:after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 0;
          height: 2px;
          background: #31378f;
          transition: width 0.3s ease;
        }

        .nav-links a:hover, .nav-link:hover {
          color: #31378f;
        }

        .nav-links a:hover:after, .nav-link:hover:after {
          width: 100%;
        }

        .nav-links a.active, .nav-link.active {
          color: #31378f;
        }

        .nav-links a.active:after, .nav-link.active:after {
          width: 100%;
        }

        /* Dropdown Styles */
        .dropdown {
          position: relative;
        }

        .dropdown-content {
          display: none;
          position: absolute;
          top: 100%;
          left: 0;
          // background: white;
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
          padding: 10px 0;
          border-radius: 8px;
          z-index: 10;
          min-width: 200px;
          animation: fadeIn 0.3s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .dropdown:hover .dropdown-content {
          display: block;
        }

        .dropdown-content a {
          display: block;
          padding: 10px 20px;
          font-size: 15px;
          font-weight: 500;
          color: #555;
          transition: all 0.3s ease;
        }

        .dropdown-content a:hover {
          // background: #f8f9fa;
          color: #31378f;
          padding-left: 25px;
        }

        .dropdown-content a.active {
          color: #31378f;
          font-weight: 600;
          // background: #f8f9fa;
        }

        /* Search Styles */
        .search-wrapper {
          display: flex;
          align-items: center;
          margin-left: 20px;
        }

        .search-form {
          display: ${searchOpen ? 'flex' : 'none'};
          align-items: center;
          background: #f8f9fa;
          border-radius: 30px;
          overflow: hidden;
          box-shadow: 0 4px 15px rgba(0,0,0,0.05);
        }

        .search-input {
          border: none;
          outline: none;
          background: transparent;
          padding: 8px 15px;
          font-size: 14px;
          width: 200px;
        }

        .search-button {
          background: linear-gradient(to bottom, #023e8a, #0077b6);
          color: white;
          border: none;
          padding: 8px 15px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .search-button:hover {
          background: #1a1d5e;
        }

        .search-icon {
          cursor: pointer;
          font-size: 18px;
          color: #333;
          margin-left: 15px;
          transition: all 0.3s ease;
        }

        .search-icon:hover {
          color: #31378f;
          transform: scale(1.1);
        }

        /* Login Button */
        .login-btn {
          background: linear-gradient(to bottom, #023e8a, #0077b6);
          color: white;
          padding: 8px 20px;
          border-radius: 30px;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.3s ease;
          margin-left: 15px;
          font-size: 15px;
          border: 2px solid #31378f;
        }

        .login-btn:hover {
          background: transparent;
          color: #31378f;
        }

        /* Mobile Menu Styles */
        .hamburger {
          display: none;
          flex-direction: column;
          justify-content: space-between;
          width: 30px;
          height: 21px;
          cursor: pointer;
          z-index: 1001;
        }

        .bar {
          width: 100%;
          height: 3px;
          background: linear-gradient(to bottom, #023e8a, #0077b6);
          border-radius: 3px;
          transition: all 0.3s ease;
        }

        .hamburger.active .bar:nth-child(1) {
          transform: translateY(9px) rotate(45deg);
        }

        .hamburger.active .bar:nth-child(2) {
          opacity: 0;
        }

        .hamburger.active .bar:nth-child(3) {
          transform: translateY(-9px) rotate(-45deg);
        }

        /* Enhanced Mobile Responsive Styles */
        @media (max-width: 1024px) {
          .nav-links {
            gap: 20px;
          }

          .top-header {
            padding: clamp(8px, 1.8vw, 10px) clamp(12px, 3vw, 15px);
          }
        }

        @media (max-width: 900px) {
          .nav-links {
            gap: 15px;
          }
          
          .search-input {
            width: 150px;
          }
        }

        @media (max-width: 768px) {
          .top-header {
            padding: clamp(6px, 1.5vw, 8px) clamp(8px, 2vw, 12px);
            gap: clamp(8px, 2vw, 12px);
          }
          
          .top-left, .top-right {
            gap: clamp(6px, 1.5vw, 10px);
          }

          .top-contact {
            font-size: clamp(10px, 1.4vw, 12px);
            gap: clamp(3px, 0.8vw, 5px);
          }

          .top-contact svg {
            font-size: clamp(10px, 1.3vw, 12px);
          }

          .social-icon {
            width: clamp(22px, 2.5vw, 26px);
            height: clamp(22px, 2.5vw, 26px);
            font-size: clamp(10px, 1.3vw, 12px);
          }

          .navbar {
            padding: 0 5%;
          }
          
          .hamburger {
            display: flex;
          }
          
          .nav-main {
            position: fixed;
            top: 0;
            left: ${menuOpen ? '0' : '-100%'};
            width: 120px;
            height: 100vh;
            background: white;
            flex-direction: column;
            align-items: flex-start;
            padding: 80px 30px 30px;
            box-shadow: 10px 0 30px rgba(0,0,0,0.1);
            transition: all 0.5s ease;
            z-index: 1000;
          }
          
          .nav-links {
            flex-direction: column;
            align-items: flex-start;
            gap: 20px;
            width: 100%;
          }
          
          .dropdown-content {
            position: static;
            box-shadow: none;
            padding-left: 15px;
            display: ${menuOpen ? 'block' : 'none'};
            animation: none;
          }
          
          .search-wrapper {
            margin: 20px 0 0;
            width: 100%;
          }
          
          .search-form {
            width: 100%;
          }
          
          .search-input {
            width: 100%;
          }
          
          .login-btn {
            margin: 20px 0 0;
            width: 100%;
            text-align: center;
          }
        }

        @media (max-width: 640px) {
          .top-header {
            flex-direction: column;
            align-items: center;
            gap: clamp(8px, 2vw, 12px);
          }
          
          .top-left, .top-right {
            width: 100%;
            justify-content: center;
          }
        }

        @media (max-width: 540px) {
          .top-header {
            padding: clamp(6px, 1.2vw, 8px) clamp(6px, 1.5vw, 10px);
          }
          
          .top-left, .top-right {
            gap: clamp(8px, 2vw, 12px);
          }

          .top-contact {
            font-size: clamp(9px, 1.2vw, 11px);
            gap: clamp(3px, 0.6vw, 4px);
          }

          .contact-text {
            max-width: 120px;
          }

          .logo-img {
            height: 60px;
          }
          
          .nav-main {
            width: 70%;
          }
        }

        @media (max-width: 480px) {
          .logo-img {
            height: 50px;
          }
          
          .nav-main {
            width: 60%;
          }
        }

        @media (max-width: 420px) {
          .top-header {
            padding: clamp(5px, 1vw, 7px) clamp(5px, 1.2vw, 8px);
          }

          .top-contact {
            font-size: clamp(8px, 1.1vw, 10px);
            gap: clamp(2px, 0.5vw, 3px);
          }

          .contact-text {
            max-width: 100px;
          }

          .social-icon {
            width: clamp(20px, 2.2vw, 24px);
            height: clamp(20px, 2.2vw, 24px);
            font-size: clamp(9px, 1.1vw, 11px);
          }

          .social-icons {
            gap: clamp(4px, 1vw, 6px);
          }
        }

        @media (max-width: 350px) {
          .top-contact .contact-text {
            display: none;
          }

          .top-contact {
            justify-content: center;
            min-width: 28px;
          }

          .social-icon {
            width: clamp(18px, 2vw, 22px);
            height: clamp(18px, 2vw, 22px);
            font-size: clamp(8px, 1vw, 10px);
          }
        }
      `}</style>

      <div className="top-header">
        <div className="top-left">
          <a href="tel:+919697988891" className="top-contact">
            <FontAwesomeIcon icon={faPhone} /> 
            <span className="contact-text">+91 9697988891</span>
          </a>
          <a href="mailto:info@travelwitheducation.com" className="top-contact">
            <FontAwesomeIcon icon={faEnvelope} /> 
            <span className="contact-text">info@travelwitheducation.com</span>
          </a>
        </div>
        <div className="top-right">
          <div className="social-icons">
            <a href="https://www.facebook.com/nmtindiaSangli?mibextid=ZbWKwL" className="social-icon">
              <FontAwesomeIcon icon={faFacebookF} />
            </a>
            <a href="https://www.instagram.com/nmt_india_holidays?igsh=eDU3Z3J5dm9jN3E4" className="social-icon">
              <FontAwesomeIcon icon={faInstagram} />
            </a>
            <a href="https://x.com/nmtindia_tours?t=5mvTEU_wMQ60xKHFAnFk-A&s=09" className="social-icon">
              <FontAwesomeIcon icon={faTwitter} />
            </a>
            <a href="#" className="social-icon">
              <FontAwesomeIcon icon={faYoutube} />
            </a>
          </div>
        </div>
      </div>

      <div className="navbar">
        <div className="navbar-container">
          <img src={logoImg} alt="NMT India Logo" className="logo-img" />

          <div
            className={`hamburger ${menuOpen ? 'active' : ''}`}
            ref={hamburgerRef}
            onClick={toggleMenu}
          >
            <div className="bar"></div>
            <div className="bar"></div>
            <div className="bar"></div>
          </div>

          <div className="nav-main" ref={menuRef}>
            <div className="nav-links">
              <NavLink to="/" onClick={() => setMenuOpen(false)} exact>Home</NavLink>
              <div className="dropdown">
                <span className={`nav-link ${isTourActive ? 'active' : ''}`}>Tour Packages</span>
                <div className="dropdown-content">
                  <NavLink to="/domestic-tours" onClick={() => setMenuOpen(false)}>Domestic Tours</NavLink>
                  <NavLink to="/international-tours" onClick={() => setMenuOpen(false)}>International Tours</NavLink>
                </div>
              </div>
              <NavLink to="/aboutus" onClick={() => setMenuOpen(false)}>About Us</NavLink>
              <NavLink to="/gallery" onClick={() => setMenuOpen(false)}>Gallery</NavLink>
              <NavLink to="/testimonials" onClick={() => setMenuOpen(false)}>Testimonials</NavLink>
              <NavLink to="/contact-us" onClick={() => setMenuOpen(false)}>Contact Us</NavLink>
            </div>

            <div className="search-wrapper">
              <form className="search-form" onSubmit={handleSearch}>
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button type="submit" className="search-button">
                  <FontAwesomeIcon icon={faSearch} />
                </button>
              </form>
              <FontAwesomeIcon 
                icon={faSearch} 
                className="search-icon" 
                onClick={toggleSearch}
              />
            </div>

            <Link to="/user-login" className="login-btn" onClick={() => setMenuOpen(false)}>
              Login
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;